import { isThemePreview } from '../../api/previewVisit';
import React from 'react';
import { collectionCardTemplate, productCardTemplate, withoutPriceParts } from '@xshop/shared';
import { ProductScope } from './ProductScope';
import { ElementNodeView, ROOT_PATH } from './ElementNode';
import { catalogRows } from './sampleCatalog';
import { placeholderCollections } from './placeholderCatalog';
import { ElementRenderProvider, useElementBox, useElementRender, type ElementComponent } from './elementRenderContext';
import { boolProp, choiceProp, numberProp, textProp } from './elementProps';
import { useStoreCollections, type StoreCollection } from './useStoreCatalog';
import { useProductListRows } from './useProductListRows';
import { CatalogNote, CatalogSkeleton, columnsStyle } from './catalogCards';
import { CatalogSlider, SLIDER_ARROWS } from './CatalogSlider';
import { ProductListEmpty } from './ProductListEmpty';
import { countWords, useShopWords } from '../../commerce/shopWords';

import './elementCatalogCards.css';
import './elementProductCard.css';
import './elementCatalogStagger.css';
import './elementCatalogMosaic.css';
import './elementCatalogBusy.css';

// The two rows whose content is the shop's own. Nothing here is typed by a merchant: the names, the
// pictures and the prices are their catalog, so a card row is right on the day they add their fourth
// collection without anyone opening the builder.

// One template is repeated under each collection's facts. Its image, text, count, and arrow remain
// ordinary selectable elements; catalog values stay linked rather than becoming static copy.
const CollectionScope: React.FC<{ row: StoreCollection; showCount: boolean; children: React.ReactNode }> = ({ row, showCount, children }) => {
  const context = useElementRender();
  const words = useShopWords();
  const base = context.store ?? { storeName: '', logoUrl: '', currency: 'MAD', moneyLocale: 'fr-MA' as const, cartCount: 0, cartTotal: 0, product: null };
  const sample = row.id.startsWith('sample-collection-');
  const collection = { title: row.title, image: row.imageUrl ?? '', href: sample ? '/collections/all' : `/collections/${row.slug}`,
    count: showCount && !sample ? countWords(row.productCount, words) : '' };
  return <ElementRenderProvider value={{ ...context, store: { ...base, collection } }}>{children}</ElementRenderProvider>;
};

// Draws the merchant's collections as cards, in a grid, a mosaic or a slider.
export const CollectionListElement: ElementComponent = ({ node, children }) => {
  const box = useElementBox(node);
  const { editing } = useElementRender();
  const limit = numberProp(node.props, 'limit', 3);
  const handles = Array.isArray(node.props.collections) ? node.props.collections.flatMap((row) =>
    row && typeof row === 'object' && typeof row.collection === 'string' && row.collection ? [row.collection] : []) : [];
  const read = useStoreCollections(limit, handles);
  const samples: StoreCollection[] = boolProp(node.props, 'sampleContent') && Array.isArray(node.props.collectionSamples)
    ? node.props.collectionSamples.slice(0, limit).flatMap((raw, index) => raw && typeof raw === 'object' && typeof raw.title === 'string' && typeof raw.src === 'string'
      ? [{ id: `sample-collection-${index}`, slug: 'all', title: raw.title, imageUrl: raw.src, productCount: 0 }] : []) : [];
  // The theme's samples in a theme preview; an installed store's own collections, and stand-ins where it has none.
  const placeholders = placeholderCollections(limit, samples.map((row) => row.title));
  const rows = catalogRows(read.data, read.status !== 'loading', samples, placeholders, isThemePreview());
  const template = node.children.length ? children : collectionCardTemplate(node.id).map((child) => <ElementNodeView key={child.id} node={child} path={ROOT_PATH} />);
  const mosaic = textProp(node.props, 'layout') === 'mosaic';
  const track = { ...box, className: [box.className, 'xe-cat'].filter(Boolean).join(' '), 'data-swipe': (!mosaic && boolProp(node.props, 'swipeOnPhone')) || undefined,
    'data-mosaic': mosaic || undefined, style: columnsStyle(numberProp(node.props, 'columns', 3)) };
  return (
    <CatalogSlider on={textProp(node.props, 'layout') === 'carousel'} editing={editing} track={track}>
      {read.status === 'loading' && rows.length === 0 && <CatalogSkeleton count={limit} />}
      {rows.map((row) => <CollectionScope key={row.id} row={row} showCount={boolProp(node.props, 'showCount', true)}>{template}</CollectionScope>)}
      {editing && read.status === 'failed' && <CatalogNote>Your collections could not be loaded. Please try again.</CatalogNote>}
    </CatalogSlider>
  );
};

// Reports whether a card sits in an even column of its row, which a staggered grid sets lower.
const isLowered = (at: number, columns: number): boolean => (at % Math.max(1, columns)) % 2 === 1;

// Draws the products row: what is inside one of the merchant's collections, as they merchandised it.
export const ProductListElement: ElementComponent = ({ node, children }) => {
  const box = useElementBox(node);
  const { editing } = useElementRender();
  const { rows, read, limit, from, stale } = useProductListRows(node.props);
  const cardNodes = node.children.length ? node.children : productCardTemplate(node.id);
  const showPrice = boolProp(node.props, 'showPrice', true);
  const template = showPrice && node.children.length ? children : (showPrice ? cardNodes : withoutPriceParts(cardNodes))
    .map((child) => <ElementNodeView key={child.id} node={child} path={ROOT_PATH} />);
  const columns = numberProp(node.props, 'columns', 4);
  const stagger = boolProp(node.props, 'stagger');
  const track = {
    ...box, className: [box.className, 'xe-cat', 'xe-cat--products'].filter(Boolean).join(' '), style: columnsStyle(columns),
    'data-stagger': stagger || undefined, 'data-swipe': boolProp(node.props, 'swipeOnPhone') || undefined,
    // While a new page of the listing is on its way, the last one stays, dimmed and marked busy.
    'aria-busy': stale || undefined, 'data-stale': stale || undefined
  };
  return (
    <CatalogSlider on={textProp(node.props, 'layout') === 'carousel'} editing={editing} track={track} arrows={choiceProp(node.props, 'arrows', SLIDER_ARROWS, 'above')}>
      {read.status === 'loading' && rows.length === 0 && <CatalogSkeleton count={limit} />}
      {rows.map((row, at) => (
        <div key={row.id} className="xe-cat-product" data-show-price={showPrice} data-lower={(stagger && isLowered(at, columns)) || undefined}>
          <ProductScope product={row}>{template}</ProductScope>
        </div>
      ))}
      {editing && read.status === 'failed' && <CatalogNote>Products could not be loaded. Please try again.</CatalogNote>}
      {rows.length === 0 && <ProductListEmpty from={from} status={read.status} editing={editing} />}
    </CatalogSlider>
  );
};
